import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Callout from './Callout';
import Step from './Step';

interface MarkdownRendererProps {
  content: string;
}

// Pre-process markdown to handle custom callout syntax
// :::tip, :::warning, :::critical, :::note
function preprocessCallouts(md: string): string {
  // Convert :::type\ncontent\n::: to HTML div markers
  return md.replace(
    /:::(tip|warning|critical|note)\n([\s\S]*?):::/g,
    (_match, type, content) => {
      return `<div data-callout="${type}">${content.trim()}</div>`;
    }
  );
}

// Resolve r2:// image URLs
function resolveR2Urls(md: string): string {
  return md.replace(/!\[([^\]]*)\]\(r2:\/\/([^)]+)\)/g, '![$1](/api/media/$2)');
}

// Detect video files by extension so image markdown (or raw <img>) pointing at a
// video renders as a <video> player instead of a broken <img>.
const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg|ogv)$/i;

// Resolve an r2:// src to the served /api/media path (raw HTML fallback).
function resolveSrc(src?: string): string | undefined {
  if (!src) return src;
  return src.startsWith('r2://') ? `/api/media/${src.slice(5)}` : src;
}

export default function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const processed = resolveR2Urls(preprocessCallouts(content));

  return (
    <div className="prose-oak">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          // Custom div handler for callouts
          div: ({ node, children, ...props }) => {
            const calloutType = (props as any)['data-callout'];
            if (calloutType) {
              return <Callout type={calloutType}>{children}</Callout>;
            }
            return <div {...props}>{children}</div>;
          },
          // Headings
          h1: ({ children }) => (
            <h1 className="text-2xl font-extrabold text-navy mb-4 mt-8 first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-bold text-navy mb-3 mt-6">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-bold text-content-primary mb-2 mt-5">{children}</h3>
          ),
          // Paragraphs
          p: ({ children }) => (
            <p className="text-content-primary leading-[1.7] mb-4 text-[15px]">{children}</p>
          ),
          // Links
          a: ({ href, children }) => (
            <a href={href} className="text-oak-green hover:underline font-medium" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
              {children}
            </a>
          ),
          // Lists
          ul: ({ children }) => (
            <ul className="list-disc pl-6 mb-4 space-y-1">{children}</ul>
          ),
          ol: ({ children }) => {
            // Render ordered lists as Step components
            const items = Array.isArray(children) ? children : [children];
            let stepNum = 0;
            return (
              <div className="mb-4">
                {items.map((child: any, i: number) => {
                  if (child?.type === 'li' || (child?.props?.children)) {
                    stepNum++;
                    return <Step key={i} number={stepNum}>{child?.props?.children || child}</Step>;
                  }
                  return child;
                })}
              </div>
            );
          },
          li: ({ children }) => (
            <li className="text-content-primary text-[15px] leading-[1.7]">{children}</li>
          ),
          // Images (and videos referenced via image markdown / r2:// URLs)
          img: ({ src, alt }) => {
            const resolvedSrc = resolveSrc(src);
            if (resolvedSrc && VIDEO_EXT.test(resolvedSrc)) {
              return (
                <video
                  src={resolvedSrc}
                  controls
                  preload="metadata"
                  className="w-full rounded-lg border border-content-border my-4"
                />
              );
            }
            return (
              <img
                src={resolvedSrc}
                alt={alt || ''}
                className="w-full rounded-lg border border-content-border my-4"
                loading="lazy"
              />
            );
          },
          // Raw <video> tags (via rehype-raw) — resolve r2:// and apply consistent styling
          video: ({ node, src, children, ...props }) => (
            <video
              {...props}
              src={resolveSrc(src as string | undefined)}
              controls
              preload="metadata"
              className="w-full rounded-lg border border-content-border my-4"
            >
              {children}
            </video>
          ),
          // Code blocks
          code: ({ className, children }) => {
            const isBlock = className?.startsWith('language-');
            if (isBlock) {
              return (
                <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto mb-4 text-sm">
                  <code>{children}</code>
                </pre>
              );
            }
            return (
              <code className="bg-surface-light text-oak-green px-1.5 py-0.5 rounded text-[13px] font-mono">
                {children}
              </code>
            );
          },
          pre: ({ children }) => <>{children}</>,
          // Blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-oak-green/30 pl-4 my-4 text-content-secondary italic">
              {children}
            </blockquote>
          ),
          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse border border-content-border rounded-lg text-sm">
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className="bg-surface-light border border-content-border px-3 py-2 text-left font-semibold text-content-primary">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-content-border px-3 py-2 text-content-primary">{children}</td>
          ),
          // Horizontal rule
          hr: () => <hr className="border-content-border my-6" />,
          // Strong/bold
          strong: ({ children }) => <strong className="font-bold text-content-primary">{children}</strong>,
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}
